import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, Trash, Warning, MagnifyingGlass, ArrowsCounterClockwise, Cube } from "@phosphor-icons/react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { formatINR } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const REASONS = [
  { v: "issued", l: "Issued to project" },
  { v: "sold", l: "Sold / dispatched" },
  { v: "damaged", l: "Damaged / lost" },
  { v: "adjustment", l: "Adjustment" },
];

const emptyOut = { description: "", hsn: "", unit: "Unit", qty: 1, reason: "issued", project: "", date: today(), notes: "" };

export default function Inventory() {
  const { active, activeId } = useCompany();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ item_count: 0, low_stock_count: 0, total_on_hand_value: 0, total_issued_value: 0 });
  const [query, setQuery] = useState("");
  const [showLowOnly, setShowLowOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyOut });
  const [history, setHistory] = useState([]);
  const [historyItem, setHistoryItem] = useState(null); // {description, hsn, item_key}
  const [historyOpen, setHistoryOpen] = useState(false);

  const load = async () => {
    if (!activeId) { setRows([]); setSummary({ item_count: 0, low_stock_count: 0, total_on_hand_value: 0, total_issued_value: 0 }); return; }
    try {
      const { data } = await api.get("/finance/inventory/items", { params: { company_id: activeId } });
      setRows(data.rows || []);
      setSummary(data.summary || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const filtered = useMemo(() => {
    let r = rows;
    if (query.trim()) {
      const q = query.toLowerCase();
      r = r.filter((x) => (x.description || "").toLowerCase().includes(q) || (x.hsn || "").toLowerCase().includes(q));
    }
    if (showLowOnly) r = r.filter((x) => x.low_stock);
    return r;
  }, [rows, query, showLowOnly]);

  const openStockOut = (preset) => {
    setForm({
      ...emptyOut,
      date: today(),
      description: preset?.description || "",
      hsn: preset?.hsn || "",
      unit: preset?.unit || "Unit",
    });
    setOpen(true);
  };

  const saveStockOut = async () => {
    if (!activeId) { toast.error("Pick a company first"); return; }
    if (!form.description.trim()) { toast.error("Item description required"); return; }
    if (!form.qty || Number(form.qty) <= 0) { toast.error("Qty must be > 0"); return; }
    try {
      await api.post("/finance/inventory/stock-out", {
        company_id: activeId,
        description: form.description.trim(),
        hsn: (form.hsn || "").trim(),
        qty: Number(form.qty),
        reason: form.reason,
        project: form.project.trim(),
        date: form.date,
        notes: form.notes.trim(),
      });
      toast.success("Stock-out recorded");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openHistory = async (row) => {
    setHistoryItem({ description: row.description, hsn: row.hsn, item_key: row.item_key });
    setHistory([]);
    setHistoryOpen(true);
    try {
      const { data } = await api.get("/finance/inventory/stock-out", { params: { company_id: activeId, item_key: row.item_key } });
      setHistory(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };

  const deleteStockOut = async (so) => {
    if (!window.confirm(`Undo this stock-out of ${so.qty} ${historyItem?.unit || ""}?`)) return;
    try {
      await api.delete(`/finance/inventory/stock-out/${so.id}`);
      toast.success("Reversed");
      setHistory((curr) => curr.filter((x) => x.id !== so.id));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Layout
      title="Inventory"
      subtitle={`${active?.name || "—"} · stock-on-hand · derived from purchases minus consumption`}
      actions={
        <>
          <button onClick={() => openStockOut(null)} data-testid="inv-stock-out-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95">
            <Plus size={16}/> Record Stock Out
          </button>
        </>
      }
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-black/10 bg-white mb-6">
        <Kpi label="Distinct items" value={String(summary.item_count || 0)} testid="inv-kpi-items"/>
        <Kpi label="On-hand value" value={formatINR(summary.total_on_hand_value || 0)} tone="ok" testid="inv-kpi-on-hand"/>
        <Kpi label="Issued (COGS realised)" value={formatINR(summary.total_issued_value || 0)} tone="warn"/>
        <Kpi label="Low / out-of-stock" value={String(summary.low_stock_count || 0)} tone={summary.low_stock_count ? "danger" : "neutral"} testid="inv-kpi-low"/>
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#717680]"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by item or HSN" className="rounded-none pl-9" data-testid="inv-search"/>
        </div>
        <label className="inline-flex items-center gap-2 text-xs font-mono-shelv uppercase tracking-widest cursor-pointer" data-testid="inv-low-toggle">
          <input type="checkbox" checked={showLowOnly} onChange={(e) => setShowLowOnly(e.target.checked)} className="accent-[#D90429]"/>
          Show low-stock only
        </label>
      </div>

      {/* Roll-up table */}
      {filtered.length === 0 ? (
        <div className="border border-black/10 bg-white p-16 text-center text-[#717680]" data-testid="inv-empty">
          <Cube size={36} className="mx-auto text-[#D90429] mb-3"/>
          <p>{rows.length === 0 ? "No inventory yet. Inventory rows appear automatically as soon as you log a purchase with kind = Inventory under Purchases · COGS." : "Nothing matches the current filter."}</p>
        </div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]" data-testid="inv-table">
            <thead className="bg-[#F5F6F8] text-left">
              <tr>
                {["Item", "HSN", "Bought", "Issued", "On-hand", "Avg rate", "On-hand value", "Last bought", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 font-mono-shelv text-[10px] uppercase tracking-widest text-[#717680]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.item_key} className={`border-t border-black/5 hover:bg-[#FAFBFC] ${r.low_stock ? "bg-[#D90429]/[0.03]" : ""}`} data-testid={`inv-row-${r.item_key}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                      {r.description}
                      {r.low_stock && (
                        <span className="text-[9px] px-1.5 py-0.5 bg-[#D90429] text-white font-mono-shelv font-bold tracking-wider inline-flex items-center gap-0.5">
                          <Warning size={9} weight="bold"/>LOW
                        </span>
                      )}
                    </div>
                    {r.last_vendor && <div className="text-[10px] text-[#717680] font-mono-shelv">last vendor: {r.last_vendor}</div>}
                  </td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[12px]">{r.hsn || "—"}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">
                    <div>{r.qty_bought} {r.unit}</div>
                    <div className="text-[10px] text-[#717680]">{formatINR(r.value_bought)}</div>
                  </td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[#F4A261]">
                    {r.qty_out > 0 ? <>{r.qty_out} {r.unit}<div className="text-[10px] text-[#717680]">{formatINR(r.issued_value)}</div></> : "—"}
                  </td>
                  <td className="px-3 py-2.5 font-mono-shelv font-bold">{r.qty_on_hand} {r.unit}</td>
                  <td className="px-3 py-2.5 font-mono-shelv">{formatINR(r.avg_rate)}</td>
                  <td className="px-3 py-2.5 font-mono-shelv font-semibold text-[#0D9488]">{formatINR(r.on_hand_value)}</td>
                  <td className="px-3 py-2.5 font-mono-shelv text-[11px]">{r.last_date || "—"}</td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => openStockOut(r)} className="text-[11px] px-2 py-1 border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white mr-1" data-testid={`inv-out-${r.item_key}`}>− Issue</button>
                    {r.out_count > 0 && (
                      <button onClick={() => openHistory(r)} className="p-1.5 hover:bg-black/5" title="History" data-testid={`inv-history-${r.item_key}`}><ArrowsCounterClockwise size={13}/></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stock-out dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Record stock out</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Item description *</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" data-testid="so-description"/></div>
            <div><Label>HSN</Label><Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="rounded-none"/></div>
            <div><Label>Qty *</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="rounded-none" data-testid="so-qty"/></div>
            <div>
              <Label>Reason</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="rounded-none"><SelectValue/></SelectTrigger>
                <SelectContent>{REASONS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Project / reference (optional)</Label><Input value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} className="rounded-none" placeholder="e.g. Work order #123 / Client name"/></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={saveStockOut} data-testid="so-save" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Record</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock-out history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Stock-out history · {historyItem?.description}</DialogTitle></DialogHeader>
          {history.length === 0 ? (
            <div className="p-6 text-center text-[#717680] text-sm">No movements yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="inv-history-table">
                <thead className="text-left text-[10px] text-[#717680] uppercase font-mono-shelv tracking-widest">
                  <tr><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Qty</th><th className="py-2 pr-3">Reason</th><th className="py-2 pr-3">Project</th><th className="py-2 pr-3">Notes</th><th></th></tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t border-black/5" data-testid={`inv-history-row-${h.id}`}>
                      <td className="py-2 pr-3 font-mono-shelv">{h.date}</td>
                      <td className="py-2 pr-3 font-mono-shelv text-[#D90429] font-semibold">−{h.qty}</td>
                      <td className="py-2 pr-3"><span className="text-[10px] font-mono-shelv uppercase tracking-widest bg-[#F4A261]/15 text-[#F4A261] px-1.5 py-0.5">{h.reason}</span></td>
                      <td className="py-2 pr-3 text-[#4A4D57] text-[12px]">
                        {h.project || "—"}
                        {h.source_type === "invoice" && (
                          <span className="ml-2 text-[9px] font-mono-shelv uppercase tracking-widest bg-[#0D9488]/15 text-[#0D9488] px-1.5 py-0.5" data-testid={`inv-auto-badge-${h.id}`}>Auto · Invoice</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-[#4A4D57] text-[12px] max-w-[16rem] truncate">{h.notes || ""}</td>
                      <td className="py-2 pr-3 text-right">
                        <button onClick={() => deleteStockOut(h)} className="p-1 hover:bg-[#D90429]/10 text-[#D90429]" title="Reverse"><Trash size={12}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setHistoryOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Kpi({ label, value, sub, tone = "neutral", testid }) {
  const colors = {
    neutral: "text-[#0A0A0A]", ok: "text-[#0D9488]",
    warn: "text-[#F4A261]", danger: "text-[#D90429]",
  };
  return (
    <div className="p-5 border-r border-black/5 last:border-r-0" data-testid={testid}>
      <div className="font-mono-shelv text-[10px] tracking-widest uppercase text-[#D90429]">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tighter mt-2 ${colors[tone] || colors.neutral}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#717680] font-mono-shelv mt-1">{sub}</div>}
    </div>
  );
}
